import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSecureIPHash } from '@/hooks/useSecureIPHash';

export const useVoteCooldown = (serverId: string) => {
  const { user } = useAuth();
  const { ipHash, ipHashAlt, isLoading: ipLoading } = useSecureIPHash();

  const canRun = useMemo(() => !!serverId && (!!user?.id || (!!ipHash && !ipLoading)), [serverId, user?.id, ipHash, ipLoading]);

  const query = useQuery({
    queryKey: ['vote-cooldown', serverId, user?.id, ipHash, ipHashAlt],
    enabled: canRun,
    refetchInterval: 60_000, // refresh every minute
    queryFn: async () => {
      // Prefer strict user-based cooldown when logged in; otherwise fallback to IP
      if (user?.id) {
        const { data, error } = await supabase
          .from('votes')
          .select('voted_at')
          .eq('server_id', serverId)
          .eq('user_id', user.id)
          .order('voted_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error && (error as any).details?.includes('No rows')) {
          return { eligible: true, secondsLeft: 0, nextTime: null as number | null };
        }
        if (error) throw error;

        if (!data) return { eligible: true, secondsLeft: 0, nextTime: null as number | null };

        const last = new Date(data.voted_at);
        const next = new Date(last.getTime() + 2 * 60 * 60 * 1000);
        const msLeft = next.getTime() - Date.now();
        const secondsLeft = Math.max(0, Math.ceil(msLeft / 1000));
        return {
          eligible: secondsLeft <= 0,
          secondsLeft,
          nextTime: Math.floor(next.getTime() / 1000)
        };
      }

      // Anonymous: IP-based cooldown if we have a hash (check both v4 and v6 hashes)
      if (ipHash) {
        const hashes = [ipHash, ipHashAlt].filter(Boolean) as string[];
        // Match if EITHER stored ip_hash or stored ip_hash_alt equals any of our hashes
        const orFilter = hashes
          .flatMap((h) => [`ip_hash.eq.${h}`, `ip_hash_alt.eq.${h}`])
          .join(',');

        const { data, error } = await supabase
          .from('votes')
          .select('voted_at')
          .eq('server_id', serverId)
          .or(orFilter)
          .order('voted_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error && (error as any).details?.includes('No rows')) {
          return { eligible: true, secondsLeft: 0, nextTime: null as number | null };
        }
        if (error) throw error;

        if (!data) return { eligible: true, secondsLeft: 0, nextTime: null as number | null };

        const last = new Date(data.voted_at);
        const next = new Date(last.getTime() + 2 * 60 * 60 * 1000);
        const msLeft = next.getTime() - Date.now();
        const secondsLeft = Math.max(0, Math.ceil(msLeft / 1000));
        return {
          eligible: secondsLeft <= 0,
          secondsLeft,
          nextTime: Math.floor(next.getTime() / 1000)
        };
      }


      return { eligible: true, secondsLeft: 0, nextTime: null as number | null };
    }
  });

  return {
    isLoading: query.isLoading || ipLoading,
    eligible: query.data?.eligible ?? true,
    secondsLeft: query.data?.secondsLeft ?? 0,
    nextTime: query.data?.nextTime ?? null,
    refetch: query.refetch
  };
};
